import { createFileRoute, Outlet, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { useUserRole } from "@/hooks/use-user-role";
import { TackLogo } from "@/components/TackLogo";
import { LogOut, Smartphone, Lock, Sparkles } from "lucide-react";
import { CurrentLocationProvider, useCurrentLocation } from "@/hooks/use-current-location";
import { LocationSwitcher } from "@/components/LocationSwitcher";
import { KioskProvider, useKioskMode } from "@/hooks/use-kiosk-mode";
import { Button } from "@/components/ui/button";
import { checkMerchantAccess } from "@/lib/staff.functions";

export const Route = createFileRoute("/_cashier")({
  component: CashierLayout,
});

function CashierLayout() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, logout } = useAuth();
  const { loading: roleLoading, role } = useUserRole();
  const navigate = useNavigate();
  // Latch so only ONE redirect ever leaves this layout. Without it, an
  // explicit logout raced a client-side navigation against a hard redirect,
  // aborting an in-flight route chunk fetch ("Failed to fetch dynamically
  // imported module") and flashing the error boundary.
  const leavingRef = useRef(false);

  const leaveToLogin = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    if (typeof window !== "undefined") window.location.replace("/login");
  }, []);


  useEffect(() => {
    if (!isLoading && !isAuthenticated) leaveToLogin();
  }, [isAuthenticated, isLoading, leaveToLogin]);

  useEffect(() => {
    if (!roleLoading && isAuthenticated && role === null) leaveToLogin();
  }, [roleLoading, role, isAuthenticated, leaveToLogin]);

  if (isLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("cashier.layoutLoading")}</div>
      </div>
    );
  }

  if (!isAuthenticated || role === null) return null;

  return (
    <CurrentLocationProvider>
      <KioskProvider>
        <CashierShell
          role={role}
          onLogout={async () => {
            // Claim the redirect BEFORE signing out so the auth-state effect
            // (and the global session-expired handler) can't fire a second
            // navigation on top of this one.
            leavingRef.current = true;
            try {
              await logout();
            } catch {
              /* local session may already be gone */
            }
            if (typeof window !== "undefined") window.location.replace("/login");
          }}
        />
      </KioskProvider>
    </CurrentLocationProvider>
  );
}


function CashierShell({ role, onLogout }: { role: "owner" | "cashier"; onLogout: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locations, current, loading } = useCurrentLocation();
  const { kiosk } = useKioskMode();
  const { session } = useAuth();

  // If a cashier is on multiple locations and hasn't picked one yet,
  // route them to the picker. Owners always see their default.
  useEffect(() => {
    if (loading) return;
    if (role !== "cashier") return;
    if (locations.length > 1 && !current) {
      navigate({ to: "/select" });
    }
  }, [loading, locations.length, current, role, navigate]);

  // Check whether the current location's owner account is active.
  // If paused → render full-screen reactivate prompt instead of cashier UI.
  const [accessState, setAccessState] = useState<{
    loading: boolean;
    locked: boolean;
    preTrial: boolean;
    ownerName: string | null;
  }>({ loading: true, locked: false, preTrial: false, ownerName: null });

  useEffect(() => {
    if (!current?.id || !session?.access_token) {
      setAccessState({ loading: false, locked: false, preTrial: false, ownerName: null });
      return;
    }
    let cancelled = false;
    setAccessState((s) => ({ ...s, loading: true }));
    checkMerchantAccess({
      data: { merchantId: current.id },
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((res) => {
        if (cancelled) return;
        setAccessState({
          loading: false,
          locked: !res.hasAccess,
          preTrial: !!res.preTrial,
          ownerName: res.ownerName,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setAccessState({ loading: false, locked: false, preTrial: false, ownerName: null });
      });
    return () => {
      cancelled = true;
    };
  }, [current?.id, session?.access_token]);

  return (
    <>
      {!kiosk && (
        <nav className="w-full bg-primary text-primary-foreground shadow-sm no-print">
          <div className="max-w-[1400px] mx-auto flex items-center gap-2 px-4 h-12">
            <div className="flex items-center gap-2 shrink-0">
              <TackLogo size="sm" />
              <span className="text-xs font-semibold uppercase tracking-wider opacity-80">{t("cashier.roleBadge")}</span>
            </div>
            {locations.length > 0 && <LocationSwitcher />}
            <div className="flex items-center gap-1 ml-auto">
              {role === "owner" && (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
                >
                  <Smartphone className="size-4" />
                  <span className="hidden sm:inline">{t("cashier.ownerView")}</span>
                </Link>
              )}
              <button
                onClick={onLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
              >
                <LogOut className="size-4" />
                <span className="hidden sm:inline">{t("cashier.logOut")}</span>
              </button>
            </div>
          </div>
        </nav>
      )}
      {accessState.locked ? (
        <CashierAccessScreen
          ownerName={accessState.ownerName}
          isOwner={role === "owner"}
          preTrial={accessState.preTrial}
          onReactivate={() => navigate({ to: "/settings", search: { tab: "billing" } as any })}
        />
      ) : (
        <Outlet />
      )}
    </>
  );
}

/**
 * Full-screen blocker shown when the location's owner account can't redeem.
 * Two distinct states:
 *  - preTrial: brand-new account, nothing was ever active → warm welcome + start trial.
 *  - otherwise: had access and lost it → "no active subscription" + reactivate.
 */
function CashierAccessScreen({
  ownerName,
  isOwner,
  preTrial,
  onReactivate,
}: {
  ownerName: string | null;
  isOwner: boolean;
  preTrial: boolean;
  onReactivate: () => void;
}) {
  const { t } = useTranslation();

  const title = preTrial ? t("cashier.preTrialTitle") : t("cashier.pausedTitle");
  const body = preTrial
    ? isOwner
      ? t("cashier.preTrialOwnerBody")
      : ownerName
        ? t("cashier.preTrialStaffBody", { owner: ownerName })
        : t("cashier.preTrialStaffBodyGeneric")
    : isOwner
      ? t("cashier.pausedOwnerBody")
      : ownerName
        ? t("cashier.pausedStaffBody", { owner: ownerName })
        : t("cashier.pausedStaffBodyGeneric");

  return (
    <div className="min-h-[calc(100vh-3rem)] flex items-center justify-center px-6 py-10">
      <div className="max-w-md w-full text-center space-y-6">
        <div
          className={
            preTrial
              ? "inline-flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary"
              : "inline-flex size-14 items-center justify-center rounded-full bg-orange-100 text-orange-700"
          }
        >
          {preTrial ? <Sparkles className="size-7" /> : <Lock className="size-7" />}
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-muted-foreground">{body}</p>
        </div>
        {isOwner && (
          <Button size="lg" onClick={onReactivate}>
            {preTrial ? t("cashier.startTrial") : t("cashier.reactivate")}
          </Button>
        )}
      </div>
    </div>
  );
}
